import { useCallback, useEffect, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Building2, Search, X, ChevronLeft, ChevronRight, ArrowUpDown, ImagePlus } from "lucide-react";
import EstructuraPresupuestal from "@/components/EstructuraPresupuestal";

export default function Empresas() {
  const { refreshUser, activeCompanyId, canEdit } = useAuth();
  const [data, setData] = useState({ items: [], total: 0, page: 1 });
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("nombre");
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [tab, setTab] = useState("datos");
  const [form, setForm] = useState({ nombre: "", zona: "", razon_social: "", id_importadora: "" });
  const [logoTs, setLogoTs] = useState(Date.now());
  const [logoErr, setLogoErr] = useState(false);
  const token = localStorage.getItem("token");

  const uploadLogo = async (file) => {
    if (!file || !edit) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/companies/${edit.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logotipo actualizado");
      setLogoErr(false);
      setLogoTs(Date.now());
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const removeLogo = async () => {
    try {
      await api.delete(`/companies/${edit.id}/logo`);
      toast.success("Logotipo eliminado");
      setLogoErr(true);
      setLogoTs(Date.now());
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const load = useCallback(() => {
    api
      .get(`/companies?search=${encodeURIComponent(search)}&sort=${sort}&order=${order}&page=${page}`)
      .then((r) => setData(r.data));
  }, [search, sort, order, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(data.total / 10));

  const toggleSort = (col) => {
    if (sort === col) setOrder(order === "asc" ? "desc" : "asc");
    else { setSort(col); setOrder("asc"); }
    setPage(1);
  };

  const openNew = () => { setEdit(null); setTab("datos"); setForm({ nombre: "", zona: "", razon_social: "", id_importadora: "" }); setOpen(true); };
  const openEdit = (c) => { setEdit(c); setTab("datos"); setLogoErr(false); setLogoTs(Date.now()); setForm({ nombre: c.nombre, zona: c.zona || "", razon_social: c.razon_social || "", id_importadora: c.id_importadora || "" }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (edit) await api.put(`/companies/${edit.id}`, form);
      else await api.post("/companies", form);
      toast.success(edit ? "Empresa actualizada" : "Empresa creada");
      setOpen(false);
      await refreshUser();
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const del = async (c) => {
    if (!window.confirm(`¿Eliminar "${c.nombre}"? Se borrarán sus datos y asignaciones.`)) return;
    try {
      await api.delete(`/companies/${c.id}`);
      toast.success("Empresa eliminada");
      await refreshUser();
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const input = "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900";
  const Th = ({ col, children }) => (
    <th className="px-4 py-3 font-medium text-left">
      <button onClick={() => toggleSort(col)} className="inline-flex items-center gap-1 hover:text-slate-900" data-testid={`emp-sort-${col}`}>
        {children} <ArrowUpDown className="h-3 w-3" />
      </button>
    </th>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="relative max-w-xs w-full">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="emp-search"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Buscar empresa, zona o razón social…"
            className="w-full rounded-md border border-slate-300 bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
          />
        </div>
        <button data-testid="emp-new" onClick={openNew} className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-800 flex items-center gap-2">
          <Plus className="h-4 w-4" /> Nueva empresa
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 border-y border-slate-200">
            <tr>
              <Th col="nombre">Nombre</Th>
              <Th col="zona">Zona</Th>
              <Th col="razon_social">Razón Social</Th>
              <th className="px-4 py-3 font-medium text-left">Id Importadora</th>
              <th className="px-4 py-3 font-medium text-right">Usuarios</th>
              <th className="px-4 py-3 font-medium text-right">Acciones</th>
            </tr>
          </thead>
          <tbody data-testid="empresas-table">
            {data.items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Sin empresas</td></tr>}
            {data.items.map((c) => (
              <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                <td className="px-4 py-3 font-medium text-slate-900 flex items-center gap-2"><Building2 className="h-4 w-4 text-slate-400" /> {c.nombre}</td>
                <td className="px-4 py-3 text-slate-600">{c.zona || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{c.razon_social || "—"}</td>
                <td className="px-4 py-3 text-slate-600 tabular-nums">{c.id_importadora || "—"}</td>
                <td className="px-4 py-3 text-right tabular-nums text-slate-600">{c.usuarios}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button data-testid={`emp-edit-${c.id}`} onClick={() => openEdit(c)} className="text-slate-400 hover:text-slate-900 mr-3"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`emp-del-${c.id}`} onClick={() => del(c)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-sm text-slate-500">
          <span>{data.total} empresa(s)</span>
          <div className="flex items-center gap-2">
            <button data-testid="emp-prev" disabled={page <= 1} onClick={() => setPage(page - 1)} className="p-1.5 rounded border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronLeft className="h-4 w-4" /></button>
            <span>Página {page} de {totalPages}</span>
            <button data-testid="emp-next" disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="p-1.5 rounded border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div className="bg-white rounded-md shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="emp-dialog">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-slate-900">{edit ? "Editar empresa" : "Nueva empresa"}</h3>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>
            {edit && (
              <div className="px-6 pt-4">
                <div className="inline-flex bg-slate-100 rounded-md p-1">
                  <button data-testid="emp-tab-datos" onClick={() => setTab("datos")} className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${tab === "datos" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>Datos</button>
                  <button data-testid="emp-tab-estructura" onClick={() => setTab("estructura")} className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${tab === "estructura" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>Estructura Presupuestal</button>
                </div>
              </div>
            )}
            {tab === "datos" ? (
              <form onSubmit={submit} className="p-6 space-y-3">
                <div>
                  <label className="text-xs text-slate-500">Nombre</label>
                  <input data-testid="emp-nombre" className={input} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} required />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Zona</label>
                  <input data-testid="emp-zona" className={input} value={form.zona} onChange={(e) => setForm({ ...form, zona: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Razón Social</label>
                  <input data-testid="emp-razon" className={input} value={form.razon_social} onChange={(e) => setForm({ ...form, razon_social: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Id Importadora</label>
                  <input data-testid="emp-id-importadora" className={input} value={form.id_importadora} onChange={(e) => setForm({ ...form, id_importadora: e.target.value })} placeholder="Captura manual" />
                </div>
                {edit && (
                  <div>
                    <label className="text-xs text-slate-500">Logotipo</label>
                    <div className="flex items-center gap-3 mt-1">
                      <div className="h-16 w-32 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                        {!logoErr ? (
                          <img data-testid="emp-logo-img" src={`${API}/companies/${edit.id}/logo?auth=${token}&t=${logoTs}`} alt="logo" className="max-h-full max-w-full object-contain" onError={() => setLogoErr(true)} />
                        ) : (
                          <span className="text-xs text-slate-400">Sin logo</span>
                        )}
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="cursor-pointer inline-flex items-center gap-1 text-sm text-slate-700 hover:text-slate-900">
                          <ImagePlus className="h-4 w-4" /> Subir imagen
                          <input data-testid="emp-logo-input" type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => uploadLogo(e.target.files?.[0])} />
                        </label>
                        {!logoErr && <button type="button" data-testid="emp-logo-remove" onClick={removeLogo} className="text-xs text-red-500 hover:text-red-700 text-left">Quitar logo</button>}
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => setOpen(false)} className="border border-slate-300 rounded-md px-4 py-2 text-sm hover:bg-slate-50">Cancelar</button>
                  <button data-testid="emp-save" className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-800">Guardar</button>
                </div>
              </form>
            ) : (
              <div className="p-6">
                {edit.id === activeCompanyId ? (
                  <EstructuraPresupuestal canEdit={canEdit} />
                ) : (
                  <div className="text-center text-sm text-slate-500 py-10" data-testid="estructura-needs-active">
                    Para configurar la estructura presupuestal de <b>{edit.nombre}</b>, selecciónala como empresa activa en la barra superior.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
